/**
 * @author: lv.chen
 * @date: 2025/4/27
 * @desc: Project详情页Tech页签页面
 */
import React, { useEffect } from 'react';
import classnames from 'classnames';
import CardSlot from '@gp/components/CardSlot';
import TechnicalModule from './components/TechnicalModule';
import EditTechnical from './components/EditTechnical';
import useRequest from '@gp/hooks/useRequest';
import usePageLog from '@gp/hooks/usePageLog';
import { fetchProjectTechnicals } from '@gp/services/projectTechnical';
import { ProjectTechnicalModule } from '@gp/enums/project';
import { TECHNICAL_MODULE_KEYS } from './const';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface ITechnicalProps extends IBaseProps {
  editable?: boolean;
  projectId?: string;
}

const getModuleTechnicals = (
  moduleKey: ProjectTechnicalModule,
  technicalModules?: IPipelineProjectTechnicalModule[] | null,
): Omit<IPipelineProjectTechnicalModule, 'module'> => {
  if (!Array.isArray(technicalModules) || !technicalModules.length) return { list: [], update_time: '' };
  const technicalModule = technicalModules.find((item) => item?.module === moduleKey);
  const { list, update_time: updateTime } = technicalModule || {};
  return { list: list || [], update_time: updateTime || '' };
};

const Technical: React.FC<ITechnicalProps> = (props) => {
  const { style, className, editable = false, projectId } = props;

  const {
    loading: technicalsResLoading,
    data: technicalsRes,
    run: technicalsResRun,
    refresh: technicalsResRefresh,
  } = useRequest(fetchProjectTechnicals, { manual: true });

  const handleEditSuccess = () => {
    technicalsResRefresh();
  };

  useEffect(() => {
    technicalsResRun({ id: projectId || '' });
  }, [projectId]);

  usePageLog({
    pageId: 'PIPELINE_DETAIL_TECHNICAL',
    uidType: 'game_code',
    uid: projectId || '',
  });

  return (
    <CardSlot
      style={style}
      className={classnames('dbg-initial-line-height dbg-pipeline-project-details-technical', className)}
      title="Technical Framework"
      loading={technicalsResLoading}
      rightDom={
        editable && (
          <EditTechnical projectId={projectId} modules={technicalsRes?.module_list} onEditSuccess={handleEditSuccess} />
        )
      }
    >
      <ul className="dbg-pipeline-project-details-technical__module-list">
        {TECHNICAL_MODULE_KEYS.map((technicalModuleKey) => {
          const { list, update_time: updateTime } = getModuleTechnicals(technicalModuleKey, technicalsRes?.module_list);
          const technicalModule: IPipelineProjectTechnicalModule = {
            module: technicalModuleKey,
            list: list || [],
            update_time: updateTime || '',
          };
          return (
            <li key={technicalModuleKey} className="dbg-pipeline-project-details-technical__module-item">
              <TechnicalModule module={technicalModule} />
            </li>
          );
        })}
      </ul>
    </CardSlot>
  );
};

export default Technical;
